/* ==========================================================================
   Go Damn — motion system v2
   --------------------------------------------------------------------------
   No GSAP. No ScrollTrigger. No CDN. No scroll listener anywhere.

   The previous version was 22.6KB and could not start until two cdnjs bundles
   (~150KB of parse + exec) had landed, during which the entire page was held
   invisible by html.gd-motion-boot. It also ran two scrubbed ScrollTriggers,
   which recompute on EVERY scroll frame — the opposite of what a smooth site
   does.

   The whole of this file's per-frame cost is zero. Everything it does happens
   either once at init, or once per element the first time that element
   crosses the viewport edge, after which the element is unobserved and its
   attributes are stripped so it is indistinguishable from plain markup.

   Compare xlncperfumery.com: one shared IntersectionObserver, one
   `observer.unobserve(target)` on first intersection, CSS keyframes on
   opacity and transform. That is the entire trick, and it is 1.9KB there.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;

  /* Old browsers get the site with no motion rather than a broken one. */
  if (!('IntersectionObserver' in window) || !document.body) return;

  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Homepage only for the press/cue extras. Matches "/" and locale-prefixed
     roots like "/en-in/". Reveals themselves run on every template. */
  var isHome = /^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?$/.test(location.pathname);
  if (isHome) root.classList.add('gd-home');

  /* Nothing has been hidden at this point — the hidden state in gd-motion.css
     hangs off [data-gd-reveal], an attribute only this file ever adds. So
     bailing out here leaves a completely normal, fully visible page. There is
     no failsafe timer that has to fire to rescue it. */
  if (reduce) return;

  function viewportHeight() {
    return window.innerHeight || root.clientHeight || 0;
  }

  /* ------------------------------------------------------------------ */
  /* Teardown                                                           */
  /*                                                                    */
  /* Once an element has finished its reveal it is returned to plain     */
  /* markup: no attribute, no class, no --gd-i, and — because the        */
  /* will-change hint in the CSS is scoped to [data-gd-reveal].gd-in —   */
  /* no compositor layer. A page that has been scrolled to the bottom    */
  /* ends up carrying exactly as much animation state as one that was    */
  /* never scrolled at all.                                              */
  /* ------------------------------------------------------------------ */
  document.addEventListener(
    'animationend',
    function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1 || !t.hasAttribute || !t.hasAttribute('data-gd-reveal')) return;
      t.removeAttribute('data-gd-reveal');
      t.classList.remove('gd-in');
      t.style.removeProperty('--gd-i');
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* One observer, for every revealed element on the page.               */
  /* ------------------------------------------------------------------ */
  var io = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.add('gd-in');
        io.unobserve(entries[i].target); /* one-shot — never watched again */
      }
    },
    /* Fires a little before the element's top edge reaches the fold, so the
       reveal is already underway by the time it is properly on screen. */
    { rootMargin: '0px 0px -8% 0px' }
  );

  /* ------------------------------------------------------------------ */
  /* Tagging, with reads and writes batched separately.                  */
  /*                                                                     */
  /* Every getBoundingClientRect() in a pass happens before any attribute */
  /* is written. Interleaving them would force the browser to re-layout   */
  /* between each one (layout thrashing) — on a homepage with ~100        */
  /* candidates that is ~100 forced synchronous layouts at load.          */
  /* ------------------------------------------------------------------ */
  var pending = [];

  function queue(el, kind, index) {
    if (!el || el.nodeType !== 1 || el.dataset.gdSeen) return;
    el.dataset.gdSeen = '1';
    pending.push({ el: el, kind: kind || '', i: index || 0 });
  }

  function flush() {
    if (!pending.length) return;
    var batch = pending;
    pending = [];

    var h = viewportHeight();

    /* READ */
    var tops = new Array(batch.length);
    for (var r = 0; r < batch.length; r++) {
      tops[r] = batch[r].el.getBoundingClientRect().top;
    }

    /* WRITE */
    for (var w = 0; w < batch.length; w++) {
      /* Anything already on screen is left completely alone. Hiding what the
         visitor is currently looking at and then animating it back in is the
         flash of content the old boot class produced. */
      if (tops[w] < h * 0.9) continue;
      var item = batch[w];
      item.el.setAttribute('data-gd-reveal', item.kind);
      if (item.i) item.el.style.setProperty('--gd-i', item.i);
      io.observe(item.el);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Auto-tagging                                                        */
  /*                                                                     */
  /* Targets stable existing selectors so no section markup has to be     */
  /* rewritten, and so a section added in the theme editor later picks    */
  /* this up for free.                                                    */
  /* ------------------------------------------------------------------ */
  var SCROLLER_SEL = '.trclone-scroll-row, .bss-row, .gdcv-row';
  var GRID_SEL = '[class*="grid"],[class*="cards"],[class*="list"],[class*="carousel"]';
  var STAGGER_CAP = 8; /* past ~8 the delay stops reading as rhythm and starts reading as lag */

  function sectionList(scope) {
    if (scope && scope.classList && scope.classList.contains('shopify-section')) return [scope];
    var main = document.getElementById('MainContent');
    if (!main) return [];
    return Array.prototype.filter.call(main.children, function (el) {
      return el.classList && el.classList.contains('shopify-section');
    });
  }

  function enhance(scope) {
    var sections = sectionList(scope);

    sections.forEach(function (section, idx) {
      if (section.dataset.gdTagged) return;
      section.dataset.gdTagged = '1';

      /* The hero runs its own bespoke entrance in CSS. */
      if (section.querySelector('.hvs')) return;
      if (!scope && idx === 0) return;
      if (section.hasAttribute('data-gd-skip')) return;

      /* (a) Horizontal scroller: heading first, then the cards settling in.
         Cards use the 'pop' variant (opacity + scale, no translate) because
         these rows compute to overflow:auto on BOTH axes, and a translateY
         would extend the scrollable area and hand the row a stray vertical
         scrollbar mid-reveal. */
      var row = section.querySelector(SCROLLER_SEL);
      if (row && row.children.length) {
        var rowHeading = section.querySelector('h2');
        if (rowHeading) queue(rowHeading, '', 0);
        Array.prototype.forEach.call(row.children, function (card, i) {
          queue(card, 'pop', Math.min(i, STAGGER_CAP) + (rowHeading ? 1 : 0));
        });
        return;
      }

      /* (b) A grid of three or more: stagger its children. */
      var grids = section.querySelectorAll(GRID_SEL);
      for (var j = 0; j < grids.length; j++) {
        if (grids[j].children.length >= 3) {
          Array.prototype.forEach.call(grids[j].children, function (child, i) {
            queue(child, '', Math.min(i, STAGGER_CAP));
          });
          return;
        }
      }

      /* (c) Otherwise the section arrives as one block. */
      queue(section, '', 0);
    });

    flush();
  }

  /* ------------------------------------------------------------------ */
  /* Header scrolled state — via a sentinel, not a scroll handler.       */
  /*                                                                     */
  /* The old version attached an un-throttled scroll listener that read   */
  /* window.scrollY and toggled a class on every scroll event. A 1px      */
  /* sentinel pinned to the top of the document gives the same signal for */
  /* free: the browser tells us when it crosses the viewport edge and is  */
  /* silent the rest of the time.                                         */
  /*                                                                     */
  /* It is appended at the END of <body> rather than prepended, so it     */
  /* cannot disturb Horizon's inline header-height script, which walks    */
  /* #header-group's siblings. Absolute positioning means DOM order has   */
  /* no effect on where it actually sits.                                 */
  /* ------------------------------------------------------------------ */
  function setupHeader() {
    var header = document.querySelector('header-component');
    if (!header) return;

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText =
      'position:absolute;top:0;left:0;width:1px;height:48px;pointer-events:none;visibility:hidden;';
    document.body.appendChild(sentinel);

    new IntersectionObserver(
      function (entries) {
        var scrolled = !entries[0].isIntersecting;
        header.classList.toggle('gd-header-scrolled', scrolled);
        if (scrolled) dismissCue();
      },
      { threshold: 0 }
    ).observe(sentinel);
  }

  /* ------------------------------------------------------------------ */
  /* Hero scroll cue                                                     */
  /*                                                                     */
  /* pointer-events:none so it can never eat a tap, dismissed on the      */
  /* first real scroll (via the header sentinel above — not a second      */
  /* listener) and unconditionally after 12s so it never becomes          */
  /* wallpaper.                                                           */
  /* ------------------------------------------------------------------ */
  var cue = null;

  function dismissCue() {
    if (!cue) return;
    var node = cue;
    cue = null;
    node.classList.remove('gd-cue-in');
    setTimeout(function () {
      if (node.parentNode) node.parentNode.removeChild(node);
    }, 600);
  }

  function setupCue(hero) {
    cue = document.createElement('span');
    cue.className = 'gd-scrollcue';
    cue.setAttribute('aria-hidden', 'true');
    hero.appendChild(cue);
    setTimeout(function () {
      if (cue) cue.classList.add('gd-cue-in');
    }, 1900);
    setTimeout(dismissCue, 12000);
  }

  /* ------------------------------------------------------------------ */
  /* Hero heading word split                                             */
  /* ------------------------------------------------------------------ */
  function splitWords(el) {
    if (el.dataset.gdSplit) return;
    var words = el.textContent.split(/\s+/).filter(Boolean);
    /* A long string is a paragraph, not a headline — shredding it into
       dozens of inline-block masks would change how it wraps. */
    if (!words.length || words.length > 24) return;

    el.textContent = '';
    words.forEach(function (word, i) {
      var line = document.createElement('span');
      line.className = 'gd-split-line';
      var span = document.createElement('span');
      span.className = 'gd-split-word';
      span.style.setProperty('--gd-i', i);
      span.textContent = word;
      line.appendChild(span);
      el.appendChild(line);
      /* The space between words is a real text node emitted BETWEEN the
         masks, never inside one: a trailing space inside an inline-block
         with overflow:hidden is collapsed at the box edge, which welds the
         whole headline into a single run ("GrowThem.HoldThe"). Keeping it
         outside also lets the heading wrap naturally. */
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.dataset.gdSplit = '1';
  }

  function setupHero() {
    var hero = document.querySelector('.hvs');
    if (!hero) {
      root.classList.add('gd-hero-ready');
      return;
    }

    var heading = hero.querySelector('.hvs-heading');
    if (heading) splitWords(heading);

    /* Added in the SAME task as the split, so the browser's first paint of
       those new spans already carries the animation. Deferring this to a
       rAF would give exactly one frame in which the words are visible in
       their un-animated position. */
    root.classList.add('gd-hero-ready');

    if (isHome) setupCue(hero);
  }

  /* ------------------------------------------------------------------ */
  /* Touch press feedback                                                */
  /*                                                                     */
  /* A JS-toggled class rather than :active, because iOS Safari only      */
  /* fires :active on elements that carry a touch handler of their own —  */
  /* a CSS-only press is invisible on the very devices this is aimed at.  */
  /* The 10px travel threshold releases the press the moment the gesture  */
  /* turns into a scroll or a carousel drag, so a card can never be left  */
  /* stuck mid-press.                                                     */
  /* ------------------------------------------------------------------ */
  var PRESS_SEL =
    '.hvs-btn,.trc-cat,.jmq-btn-light,.jmq-btn-outline,.jmq-linkbtn,.jmq-backbtn,.trclone-card,.bss-item';

  function setupPress() {
    if (!isHome) return;

    var active = null;
    var sx = 0;
    var sy = 0;

    function clear() {
      if (!active) return;
      active.classList.remove('gd-press');
      active = null;
    }

    document.addEventListener(
      'pointerdown',
      function (e) {
        clear();
        var t = e.target && e.target.closest ? e.target.closest(PRESS_SEL) : null;
        if (!t) return;
        active = t;
        sx = e.clientX;
        sy = e.clientY;
        t.classList.add('gd-press');
      },
      { passive: true }
    );

    document.addEventListener(
      'pointermove',
      function (e) {
        if (!active) return;
        if (Math.abs(e.clientX - sx) > 10 || Math.abs(e.clientY - sy) > 10) clear();
      },
      { passive: true }
    );

    /* pointercancel is what the browser fires the instant a touch turns into
       a scroll, so no scroll listener is needed to release the press. */
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      document.addEventListener(ev, clear, { passive: true });
    });
    window.addEventListener('blur', clear);
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */
  function init() {
    try {
      setupHero();
      enhance();
      setupHeader();
      setupPress();
    } catch (err) {
      /* Nothing may ever be left invisible because of a bug in this file. */
      root.classList.add('gd-hero-ready');
      Array.prototype.forEach.call(document.querySelectorAll('[data-gd-reveal]'), function (el) {
        el.removeAttribute('data-gd-reveal');
      });
      if (window.console && console.warn) console.warn('[gd-motion]', err);
    }

    /* Theme editor: a re-rendered section arrives untagged. */
    document.addEventListener('shopify:section:load', function (e) {
      try {
        enhance(e.target);
      } catch (err) {}
    });

    /* Safety sweep: anything still hidden while sitting inside the viewport
       is shown outright. Two cases this covers:

         - an element was observed while it had no height (a lazy image above
           it had not loaded yet), so its entry never reported as
           intersecting even though it ended up on screen;

         - the page was opened in a BACKGROUND tab. A hidden tab's rendering
           loop is throttled, and IntersectionObserver callbacks are
           delivered from that loop — so nothing fires at all until the tab
           is actually looked at. A single timed sweep can therefore burn
           itself out while the tab is still hidden and leave real sections
           blank, which is why this repeats on visibilitychange instead of
           running once. */
    function sweep() {
      var h = viewportHeight();
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-gd-reveal]:not(.gd-in)'),
        function (el) {
          var rect = el.getBoundingClientRect();
          if (rect.top < h && rect.bottom > 0) el.classList.add('gd-in');
        }
      );
    }

    setTimeout(sweep, 4000);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) return;
      /* The observer will normally fire on its own within a frame of the tab
         becoming visible; this is the net under that. */
      setTimeout(sweep, 4000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
